/**
 * Отправка клиентских ошибок на свой сервер.
 *
 * Sentry в проекте подключён, но включается только с VITE_SENTRY_DSN — без него
 * падения у гостей и кассира не видит никто, и о белом экране узнаёшь звонком
 * из зала. Этот канал не требует внешнего сервиса: ошибка уходит в /api/client-error
 * и попадает в те же логи pm2, куда и так смотрят.
 *
 * Личных данных не отправляем: только текст ошибки, стек, адрес страницы и
 * браузер. Ни состава заказа, ни имён гостей.
 */

type ErrorKind = 'boundary' | 'window' | 'promise';

/** Один и тот же баг в цикле не должен залить лог. */
const seen = new Set<string>();
const MAX_PER_SESSION = 5;
let sentCount = 0;

export function reportClientError(kind: ErrorKind, error: unknown, extra?: string): void {
  try {
    if (sentCount >= MAX_PER_SESSION) return;

    const err = error as any;
    const message = String(err?.message ?? err ?? 'unknown').slice(0, 500);
    const stack = String(err?.stack ?? extra ?? '').slice(0, 2000);

    // Ключ — сообщение плюс первая строка стека: одна и та же поломка при
    // каждом ре-рендере иначе улетала бы десятками одинаковых записей.
    const key = `${kind}|${message}|${stack.split('\n')[1] || ''}`;
    if (seen.has(key)) return;
    seen.add(key);
    sentCount++;

    const body = JSON.stringify({
      kind,
      message,
      stack,
      url: location.pathname + location.search,
    });

    // sendBeacon переживает закрытие вкладки — падение часто последнее, что
    // происходит на странице.
    if (navigator.sendBeacon) {
      navigator.sendBeacon('/api/client-error', new Blob([body], { type: 'application/json' }));
    } else {
      void fetch('/api/client-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // Отчёт об ошибке не имеет права сам ронять приложение.
  }
}

/** Глобальные ловушки: то, что не поймал React-boundary. */
export function installGlobalErrorReporting(): void {
  window.addEventListener('error', (e) => {
    // Ошибки загрузки картинок и прочих ресурсов сюда тоже прилетают,
    // но у них нет error — от них шум без пользы.
    if (e.error) reportClientError('window', e.error);
  });
  window.addEventListener('unhandledrejection', (e) => {
    reportClientError('promise', e.reason);
  });
}
